import { useCallback, useEffect, useRef, useState } from 'react'
import { useVTTStore } from '@/store'
import type { MeasurementShape } from '@/types'

/**
 * World-space coordinate (i.e. coords in the same space as token positions
 * BEFORE the PIXI viewport scale/translate is applied).
 */
export interface RulerCoord {
  x: number
  y: number
}

export interface RulerState {
  /** Whether ruler mode is currently active (cursor is crosshair, mouse is intercepted). */
  isActive: boolean
  /** Origin of the current measurement, or null if no measurement in flight. */
  start: RulerCoord | null
  /** Endpoint of the current measurement (live during drag, frozen during fade). */
  end: RulerCoord | null
  /** True between mousedown and mouseup. */
  isDragging: boolean
  /** Active measurement-template shape. Defaults to 'line' (classic
   *  straight ruler). Sourced from the store so the Toolbar selector and
   *  the canvas render stay in lockstep. */
  shape: MeasurementShape
}

export interface UseRulerReturn extends RulerState {
  /** Toggle ruler mode on/off. Cancels any in-flight measurement. */
  setActive: (active: boolean) => void
  /** Begin a measurement at the given world-space coord. */
  beginDrag: (coord: RulerCoord) => void
  /** Update the endpoint of the current measurement (during drag). */
  updateDrag: (coord: RulerCoord) => void
  /** End the current measurement; freezes the line then auto-clears after fadeMs. */
  endDrag: (coord: RulerCoord) => void
  /** Cancel everything immediately: clear coords, cancel timeout. */
  cancel: () => void
  /** Set the active measurement-template shape (line/cone/circle/square). */
  setShape: (shape: MeasurementShape) => void
}

const DEFAULT_FADE_MS = 1500

/**
 * useRuler — owns ruler-tool measurement state for VTTCanvas.
 *
 * `isActive` is derived from `store.toolMode === 'ruler'`, so the ruler
 * participates in the same mutually-exclusive tool radio group as
 * select/barrier/fog. `setActive(true/false)` writes `'ruler'` or
 * `'select'` to the store; the effect below clears in-flight
 * measurements whenever the tool deactivates.
 *
 * Measurement state machine (unchanged):
 *   inactive  ->                          -> active (no drag)
 *   active    -> beginDrag(p0)            -> dragging
 *   dragging  -> updateDrag(p)            -> dragging (end coord updated)
 *   dragging  -> endDrag(p)               -> fading (timeout will clear)
 *   fading    -> beginDrag / cancel /     -> previous timeout cancelled
 *                map switch / unmount
 *
 * Auto-clear timeout is stored in a ref and is cancelled on:
 *   - any new beginDrag (start of next measurement)
 *   - explicit cancel() (e.g. Esc, map switch)
 *   - tool-mode flip away from 'ruler' (any other tool selection)
 *   - component unmount (useEffect cleanup)
 */
export function useRuler(fadeMs: number = DEFAULT_FADE_MS): UseRulerReturn {
  const isActive = useVTTStore((s) => s.toolMode === 'ruler')
  const setToolMode = useVTTStore((s) => s.setToolMode)
  const shape = useVTTStore((s) => s.measurementShape)
  const setShape = useVTTStore((s) => s.setMeasurementShape)
  const [start, setStart] = useState<RulerCoord | null>(null)
  const [end, setEnd] = useState<RulerCoord | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearFadeTimeout = useCallback(() => {
    if (fadeTimeoutRef.current !== null) {
      clearTimeout(fadeTimeoutRef.current)
      fadeTimeoutRef.current = null
    }
  }, [])

  const cancel = useCallback(() => {
    clearFadeTimeout()
    setStart(null)
    setEnd(null)
    setIsDragging(false)
  }, [clearFadeTimeout])

  // Any tool change away from ruler clears the in-flight measurement so
  // a stale segment doesn't linger on the canvas after the GM grabs
  // the barrier tool.
  useEffect(() => {
    if (!isActive) cancel()
  }, [isActive, cancel])

  const setActive = useCallback(
    (active: boolean) => {
      // Forward to the unified tool-mode store. Falling back to 'select'
      // when deactivating means clicking the ruler twice in a row
      // returns the cursor to the default tool — Foundry-style.
      setToolMode(active ? 'ruler' : 'select')
    },
    [setToolMode],
  )

  const beginDrag = useCallback(
    (coord: RulerCoord) => {
      // Any new mouse-down cancels a pending fade for the previous measurement.
      clearFadeTimeout()
      setStart(coord)
      setEnd(coord)
      setIsDragging(true)
    },
    [clearFadeTimeout],
  )

  const updateDrag = useCallback((coord: RulerCoord) => {
    setEnd(coord)
  }, [])

  const endDrag = useCallback(
    (coord: RulerCoord) => {
      setEnd(coord)
      setIsDragging(false)
      clearFadeTimeout()
      fadeTimeoutRef.current = setTimeout(() => {
        fadeTimeoutRef.current = null
        setStart(null)
        setEnd(null)
      }, fadeMs)
    },
    [clearFadeTimeout, fadeMs],
  )

  // Cleanup on unmount: cancel any pending fade so we don't setState
  // on an unmounted component (Test Plan: "unmount mid-fade -> no warnings").
  useEffect(() => {
    return () => {
      clearFadeTimeout()
    }
  }, [clearFadeTimeout])

  return {
    isActive,
    start,
    end,
    isDragging,
    shape,
    setActive,
    beginDrag,
    updateDrag,
    endDrag,
    cancel,
    setShape,
  }
}

/**
 * D&D 5e diagonal distance: every other diagonal counts as 2 squares.
 * Returns squares (rounded) and feet (5 ft per square).
 */
export function computeRulerDistance(
  start: RulerCoord,
  end: RulerCoord,
  gridSize: number,
): { squares: number; feet: number } {
  const dx = Math.abs(end.x - start.x) / gridSize
  const dy = Math.abs(end.y - start.y) / gridSize
  const min = Math.min(dx, dy)
  const max = Math.max(dx, dy)
  const squares = Math.round(max + Math.floor(min / 2))
  const feet = squares * 5
  return { squares, feet }
}

/**
 * Snap a world-space coordinate to the center of the nearest grid square,
 * accounting for the map's grid offset.
 */
export function snapToGridCenter(
  coord: RulerCoord,
  gridSize: number,
  gridOffsetX: number = 0,
  gridOffsetY: number = 0,
): RulerCoord {
  const relX = coord.x - gridOffsetX
  const relY = coord.y - gridOffsetY
  const cellX = Math.floor(relX / gridSize)
  const cellY = Math.floor(relY / gridSize)
  return {
    x: cellX * gridSize + gridSize / 2 + gridOffsetX,
    y: cellY * gridSize + gridSize / 2 + gridOffsetY,
  }
}
